'use client';

import { type ReactElement, type ReactNode, cloneElement, useId, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Tooltip on `popover="manual"`: never the only label, 300ms delay, no
 * tooltip on touch. The child must accept ref and event handlers.
 */
export function Tooltip({
  label,
  children,
  side = 'top',
  className,
}: {
  label: ReactNode;
  children: ReactElement<Record<string, unknown>>;
  side?: 'top' | 'bottom';
  className?: string;
}) {
  const id = useId();
  const ref = useRef<HTMLElement | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  const timer = useRef<number | null>(null);
  const [style, setStyle] = useState<React.CSSProperties>({});

  function show() {
    if (window.matchMedia('(hover: none)').matches) return;
    timer.current = window.setTimeout(() => {
      const t = ref.current;
      const p = panel.current;

      if (!t || !p) return;
      p.showPopover();
      const r = t.getBoundingClientRect();
      const pw = p.offsetWidth;
      const ph = p.offsetHeight;
      const left = Math.max(8, Math.min(r.left + r.width / 2 - pw / 2, window.innerWidth - pw - 8));
      const top = side === 'top' ? r.top - ph - 6 : r.bottom + 6;
      setStyle({ position: 'fixed', left, top, margin: 0 });
    }, 300);
  }

  function hide() {
    if (timer.current) window.clearTimeout(timer.current);

    if (panel.current?.matches(':popover-open')) panel.current.hidePopover();
  }

  const child = cloneElement(children, {
    ref,
    'aria-describedby': id,
    // SAFETY: The surrounding typed contract establishes the asserted representation.
    onPointerEnter: (e: unknown) => {
      (/* SAFETY: The surrounding typed contract establishes the asserted representation. */ children.props.onPointerEnter as ((e: unknown) => void) | undefined)?.(e);
      show();
    },
    // SAFETY: The surrounding typed contract establishes the asserted representation.
    onPointerLeave: (e: unknown) => {
      (/* SAFETY: The surrounding typed contract establishes the asserted representation. */ children.props.onPointerLeave as ((e: unknown) => void) | undefined)?.(e);
      hide();
    },
    // SAFETY: The surrounding typed contract establishes the asserted representation.
    onFocus: (e: unknown) => {
      (/* SAFETY: The surrounding typed contract establishes the asserted representation. */ children.props.onFocus as ((e: unknown) => void) | undefined)?.(e);
      show();
    },
    // SAFETY: The surrounding typed contract establishes the asserted representation.
    onBlur: (e: unknown) => {
      (/* SAFETY: The surrounding typed contract establishes the asserted representation. */ children.props.onBlur as ((e: unknown) => void) | undefined)?.(e);
      hide();
    },
    // SAFETY: The surrounding typed contract establishes the asserted representation.
    onKeyDown: (e: KeyboardEvent) => {
      (/* SAFETY: The surrounding typed contract establishes the asserted representation. */ children.props.onKeyDown as ((e: unknown) => void) | undefined)?.(e);

      if (e.key === 'Escape') hide();
    },
  });

  return (
    <>
      {child}
      <div
        id={id}
        ref={panel}
        role="tooltip"
        popover="manual"
        style={style}
        className={cn(
          'pointer-events-none max-w-64 rounded-[6px] bg-ink px-2 py-1 text-xs font-medium text-canvas shadow-md',
          className
        )}
      >
        {label}
      </div>
    </>
  );
}
